import { SinglePromoCodeState } from 'src/domains/casino/webview/bonus-engine/campaign/promo-code/single-promo-code/SinglePromoCode.state';
import { makeAutoObservable } from 'mobx';
import { PromoCodeViewType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { checkIsNumber } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { Common } from 'src/AppState/Common';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { FormHelper, FormHelperFormType } from 'src/domains/casino/webview/bonus-engine/campaign/FormHelper';

const preventDuplicatesForCampaignStatus = ['ready', 'active'];

export class PromoCodesState {
    private localCodeList: Array<SinglePromoCodeState> = [];
    private campaignStore: CampaignStore;

    private form: FormHelperFormType;

    public constructor(
        private readonly common: Common,
        private readonly campaignId: string | undefined
    ) {
        this.campaignStore = CampaignStore.get(this.common);
        this.form = FormHelper.form;
        this.setInitialValues();

        makeAutoObservable(this);
    }

    public setInitialValues = (): void => {
        this.localCodeList = this.apiCodeList;
    };

    private get campaignStatus(): 'draft' | 'ready' | 'active' | 'paused' | 'expired' | undefined {
        if (this.campaignId === undefined) {
            return undefined;
        }
        const campaign = this.campaignStore.getCampaign(this.campaignId);

        if (campaign === null) {
            return undefined;
        }

        return campaign.status;
    }

    private get apiCodeList(): Array<SinglePromoCodeState> {
        if (this.campaignId === undefined) {
            return [];
        }
        const campaign = this.campaignStore.getCampaign(this.campaignId);

        if (campaign === null) {
            return [];
        }

        return campaign.promoCodes.map((code) => new SinglePromoCodeState(this.common, code, this.getCodeNames));
    }

    public get codeList(): Array<SinglePromoCodeState> {
        const isCloneCampaign = $appState.routerBackoffice.bonusCampaignCloneIdParam !== null;
        if (
            (this.campaignStatus === 'active' ||
                this.campaignStatus === 'paused' ||
                this.campaignStatus === 'expired' ||
                this.campaignStatus === 'ready') &&
            !isCloneCampaign
        ) {
            return this.apiCodeList.sort((a, b) => a.promoCodeName.value.localeCompare(b.promoCodeName.value));
        }

        return this.localCodeList;
    }

    public isEmpty = (): boolean => {
        return this.codeList.length === 0;
    };

    public addPromoCode = (): void => {
        this.localCodeList.push(new SinglePromoCodeState(this.common, null, this.getCodeNames, this.campaignId));
        for (const codes of this.localCodeList) {
            codes.setIsVisited();
        }
    };

    public addPromoCodesFromCSV = (csvCodes: Array<PromoCodeViewType>): void => {
        const codes = csvCodes.map((code) => new SinglePromoCodeState(this.common, code, this.getCodeNames));
        this.localCodeList = this.localCodeList
            .concat(codes)
            .filter((code) => code.promoCodeName.value !== '' || code.useLimit.value !== '');
    };

    public deletePromoCode = (id: string): void => {
        this.localCodeList = this.localCodeList.filter((promo) => promo.id !== id);
    };

    public clearPromoCodes = (): void => {
        this.localCodeList = [];
    };

    private getCodeNames = (): Array<string> => {
        const allCodeNames: Array<string> = [];

        const filteredCampaigns = this.campaignStore.allCampaigns
            .filter((campaign) => preventDuplicatesForCampaignStatus.includes(campaign.status))
            .filter((campaign) => {
                const isCloned = $appState.routerBackoffice.bonusCampaignCloneIdParam !== null;
                if (isCloned) {
                    return true;
                }
                return campaign.id !== this.campaignId;
            });

        for (const campaign of filteredCampaigns) {
            for (const code of campaign.promoCodes) {
                allCodeNames.push(code.promoCode);
            }
        }
        for (const code of this.localCodeList) {
            allCodeNames.push(code.promoCodeName.value);
        }

        return allCodeNames;
    };

    public validateForm(): boolean {
        for (const codes of this.localCodeList) {
            codes.setIsVisited();
        }

        return this.localCodeList.every((code) => code.isFormValid);
    }

    public setIsTouched(): void {
        this.localCodeList.forEach((code) => {
            code.setIsTouched();

            if (code.isTouched() && this.isTouched() === false) {
                FormHelper.setIsTouched(this.form);
            }
        });
    }

    public isTouched(): boolean {
        return this.form.isTouched();
    }

    public resetIsTouched(): void {
        this.localCodeList.forEach((code) => {
            code.resetIsTouched();
        });

        FormHelper.resetIsTouched(this.form);
    }

    public resetIsVisited(): void {
        this.localCodeList.forEach((code) => code.resetIsVisited());
    }

    public get formErrorsCount(): number {
        let errors = 0;

        for (const code of this.localCodeList) {
            errors += code.formErrorsCount;
        }

        return errors;
    }

    public get promoCodes(): Array<PromoCodeViewType> {
        if (this.localCodeList.length === 0) {
            return [];
        }
        const codes: Array<PromoCodeViewType> = [];

        for (const promo of this.localCodeList) {
            const useLimit = checkIsNumber(promo.useLimit.value);
            codes.push({
                promoCode: promo.promoCodeName.value,
                useLimit: useLimit,
                isPaused: promo.isPaused,
            });
        }

        return codes;
    }
}
